import { Button } from "@svea/design-system-components-react";
import { formatSsn, Pill, Stack, Text, useText } from "@sveabank/cc-common";
import { useState } from "react";
import Image from "../../../../components/image";
import Paper from "../../../../components/paper";
import { usePageContext } from "../../../../hooks/context";
import type { ClcDebitCard, ClcPerson } from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { ClcConfigCardModal } from "./config-card-modal";
import ClcPersonRoles from "./person-roles";

type Props = {
	person: ClcPerson;
	existingCard?: ClcDebitCard;
};

const CardContainerComponent = ({
	isApplicant,
	children,
}: {
	isApplicant: boolean;
	children: React.ReactNode;
}) =>
	isApplicant ? (
		<Stack>{children}</Stack>
	) : (
		<Paper className="bg-background-tertiary">{children}</Paper>
	);

const ClcCardholder = ({ person, existingCard }: Props) => {
	const {
		applicationId,
		clcApplicationDetails: { applicant, companyRegistration },
		removeClcAddons,
		selectedPackage,
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();
	const [isConfigCardModalOpen, setIsConfigCardModalOpen] = useState(false);

	const isApplicant = person.nationalId === applicant.nationalId;
	const isForetagarnaPackage = selectedPackage?.packageId === "FÖRETAGARNA-SVEA-STARTA-BOLAG";

	const packages = texts.newCompany.packages;
	const text = packages[selectedPackage?.packageId];

	return (
		<>
			<Paper className={isApplicant ? "bg-background-tertiary" : "bg-background-transparent"}>
				<Stack gap={4} align="start">
					<Stack>
						<Text bold>{`${person.firstName} ${person.lastName}`}</Text>
						<Text>{formatSsn(person.nationalId)}</Text>
						<ClcPersonRoles person={person} />
					</Stack>

					{existingCard ? (
						<CardContainerComponent isApplicant={isApplicant}>
							<Stack gap={2} align="start">
								<Stack direction="row" fullSize justify="between">
									<Stack direction="row" gap={2}>
										<Text bold>{t(text.debitCard)}</Text>
										{isApplicant && <Pill status="info" text={t(text.cardIncluded)} />}
									</Stack>
									<Stack direction="row" align="end" justify="end">
										{existingCard.isConfigured && (
											<Button
												variant="icon"
												icon="pencil"
												aria-label={t(texts.common.change)}
												onPress={() => setIsConfigCardModalOpen(true)}
											/>
										)}
										{!isApplicant && (
											<Button
												variant="icon"
												icon="x"
												aria-label={t(texts.common.remove)}
												onPress={() =>
													removeClcAddons({
														applicationId,
														addons: [existingCard.productId],
													})
												}
											/>
										)}
									</Stack>
								</Stack>
								<Stack direction="row" gap={2}>
									<Image
										imageSrc={isForetagarnaPackage ? "FORETAGARNA_DEBIT" : "DEBITCARD"}
										className="h-12"
										altText={t(text.debitCard)}
									/>
									<Stack>
										<Text
											bold
											className={
												existingCard.isConfigured ? "text-text-primary" : "text-text-error"
											}
										>
											{existingCard.isConfigured
												? existingCard.displayName
												: t(texts.newCompany.products.cardDisplayNameMissing)}
										</Text>
										<Text>{companyRegistration.value?.proposedCompanyNames[0] || ""}</Text>
									</Stack>
								</Stack>
								{!existingCard.isConfigured && (
									<Button variant="secondary" onPress={() => setIsConfigCardModalOpen(true)}>
										{t(texts.newCompany.products.selectDisplayName)}
									</Button>
								)}
							</Stack>
						</CardContainerComponent>
					) : (
						<Button
							variant="plain"
							iconStart="plusCircle"
							onPress={() => setIsConfigCardModalOpen(true)}
						>
							{t(text.addDebitCard)}
						</Button>
					)}
				</Stack>
			</Paper>
			<ClcConfigCardModal
				isOpen={isConfigCardModalOpen}
				setOpen={setIsConfigCardModalOpen}
				person={person}
				cardId={existingCard?.productId}
			/>
		</>
	);
};

export default ClcCardholder;
